import { useEffect, useMemo } from "react";
import { useIntake, IntakeShell } from "./IntakeShell";
import { useGeo } from "../../lib/geo";
import { CTA_AUTO_LOAN, CTA_REFINANCE } from "../../lib/ctaVocabulary";

/**
 * Category-aware auto-loan intake.
 *
 * Iter-196.PP — Form-friction simplification (2026-02-16). Reduced from
 * 5–6 fields per loan type down to 1–2 fields (credit_band + optional
 * loan_balance on refi only). Affiliate partners re-ask the rest of
 * the profile (purchase price, monthly budget, vehicle details) on
 * their own intake screens, so collecting it twice was friction
 * without conversion lift. ZIP is auto-filled silently from geo so
 * the backend's `zip_code` schema requirement still passes; the
 * sentinel "00000" is used as a fallback when geo is unavailable.
 *
 * Retired fields (still accepted by the backend `intake` dict for
 * backward compatibility):
 *   - purchase_amount, monthly_budget, down_payment (new/used)
 *   - current_payment, current_apr, remaining_term (refinance)
 *
 *   - `defaultLoanType="new"`        → new-purchase fields
 *   - `defaultLoanType="used"`       → used-purchase fields
 *   - `defaultLoanType="refinance"`  → refinance fields (existing-loan focus)
 */

const CREDIT_BANDS = [
  { id: "excellent", label: "Excellent (740+)" },
  { id: "good", label: "Good (670–739)" },
  { id: "fair", label: "Fair (580–669)" },
  { id: "poor", label: "Building (<580)" },
  { id: "unsure", label: "Not sure" },
];

// Per-loan-type metadata: backend category, step labels, submit CTA,
// post-submit context line. Kept side-by-side so onboarding flow tweaks
// only need one edit.
//
// Iter-185.BI — Per-vertical submit CTAs aligned to the user-preferred
// vocabulary set (Compare Insurance Quotes / Find Loan Offers / Lower
// My Payment / Compare Protection Plans). The pre-iter-182 generic
// CTAs drifted from the homepage hero ZIP-form vocabulary in
// QuoteForm.jsx; this unifies the CTA voice across every modal-
// rendered intake.
const LOAN_META = {
  new: {
    category: "auto-loan-new",
    submit: CTA_AUTO_LOAN,
    context:
      "Compare APRs, monthly payments, and financing offers from lenders and credit unions before visiting the dealership.",
  },
  used: {
    category: "auto-loan-used",
    submit: CTA_AUTO_LOAN,
    context:
      "Compare financing offers from lenders and credit unions before agreeing to dealer financing.",
  },
  refinance: {
    category: "refinance",
    submit: CTA_REFINANCE,
    context:
      "Compare refinance offers from lenders that may reduce your APR, lower your payment, or shorten your loan term.",
  },
};

// Iter-196.PP — ZIP is no longer a visible field. The backend
// `quote-requests` schema requires `zip_code` (min_length 3), so we
// pre-seed a sentinel value and replace it with the geo-detected ZIP
// when available. Iter-150 analytics filters strip "00000" from
// regional rollups so this doesn't pollute the geo dashboards.
function buildDefaults(loanType) {
  if (loanType === "refinance") {
    return {
      credit_band: "",
      current_balance: null,
      zip_code: "00000",
    };
  }
  return {
    credit_band: "",
    zip_code: "00000",
  };
}

function buildValidator(loanType) {
  return function validate(values) {
    if (!values.credit_band) return "Pick a credit band (or 'Not sure')";
    // ZIP is auto-filled silently — only validate when the geo
    // detector resolved a value and the user somehow overwrote it.
    if (values.zip_code && !/^\d{5}$/.test(values.zip_code)) {
      return "ZIP code must be 5 digits";
    }
    if (loanType === "refinance") {
      // Loan balance is optional. Only validate when provided.
      if (
        values.current_balance !== null &&
        values.current_balance !== undefined &&
        values.current_balance !== "" &&
        values.current_balance < 1000
      ) {
        return "Current loan balance must be at least $1,000";
      }
    }
    return null;
  };
}

export default function IntakeAutoLoan({ defaultLoanType = "new" }) {
  // Normalize to one of the three known types — guards against a stray
  // legacy value from older intake-shell localStorage drafts.
  const loanType =
    defaultLoanType === "refinance" || defaultLoanType === "used"
      ? defaultLoanType
      : "new";
  const meta = LOAN_META[loanType];

  // Defaults + validator are loan-type-specific. Memoized so React doesn't
  // see new identities on every render and discard the intake draft.
  const defaults = useMemo(() => buildDefaults(loanType), [loanType]);
  const validate = useMemo(() => buildValidator(loanType), [loanType]);

  const state = useIntake({
    category: meta.category,
    defaults,
    validate,
    contextLine: meta.context,
  });
  const { values, setField } = state;
  const { geo } = useGeo();

  // Iter-196.PP — Auto-fill ZIP from geo silently in the background so
  // the partner row still carries a routable region without making the
  // user type one in. Falls back to the "00000" sentinel from defaults.
  useEffect(() => {
    if (!geo?.zip) return;
    if (values.zip_code && values.zip_code !== "00000") return;
    if (/^\d{5}$/.test(String(geo.zip))) setField("zip_code", String(geo.zip));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo]);

  return (
    <IntakeShell
      category={meta.category}
      state={state}
      submitLabel={meta.submit}
    >
      <Field
        label="Estimated credit range"
        helper={
          loanType === "refinance"
            ? "Refi qualification ranges. 'Not sure' is fine — we'll still match you."
            : "Used for soft pre-qual matching only. No hard credit pull until you accept an offer."
        }
      >
        <select
          value={values.credit_band || ""}
          onChange={(e) => setField("credit_band", e.target.value)}
          data-testid="intake-credit-band"
          className="w-full h-12 px-3 bg-white border border-slate-300 focus:border-slate-950 outline-none text-sm font-semibold text-slate-950"
        >
          <option value="" disabled>
            Pick a credit band
          </option>
          {CREDIT_BANDS.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label}
            </option>
          ))}
        </select>
      </Field>

      {loanType === "refinance" && (
        <Field
          label="Current loan balance (optional)"
          helper="Helps lenders quote a real refi rate. Skip it to compare general offers."
        >
          <CurrencyInput
            value={values.current_balance}
            onChange={(v) => setField("current_balance", v)}
            placeholder="$18,000"
            testid="intake-current-balance"
          />
        </Field>
      )}
    </IntakeShell>
  );
}

// Iter-169 — CurrencyInput retained (step={1}) so the refinance
// loan-balance optional field accepts any whole-dollar amount.
function CurrencyInput({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-semibold">
        $
      </span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        // Iter-169 — Was step={50}, which fired the browser's native
        // "two nearest valid values are 400 and 450" error when users
        // entered an off-50 payment (e.g., $437). Customers should be
        // able to enter any whole dollar amount.
        step={1}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : +e.target.value)
        }
        placeholder={placeholder}
        data-testid={testid}
        className="w-full h-12 pl-7 pr-3 bg-white border border-slate-300 focus:border-slate-950 outline-none text-sm font-semibold text-slate-950"
      />
    </div>
  );
}

function Field({ label, helper, children }) {
  // Iter-275.AC (Phase B-2) — Wrap `{children}` inside the `<label>` so the
  // form control becomes implicitly associated with its label. Previously
  // the `<label>` was a sibling with no `htmlFor`, leaving the inner
  // `<select>` / `<input>` unlabelled to assistive tech AND breaking the
  // "click-the-label-to-focus-the-control" behaviour. The label-text now
  // sits in its own `<span>` so we can still style the label uppercase
  // while the children retain their original styling.
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
        {label}
      </span>
      {children}
      {helper && (
        <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
          {helper}
        </p>
      )}
    </label>
  );
}
